export type FlowType = "EXPORTER_LEAD" | "IMPORTER_LEAD";

export interface SignatureHistory {
  id: number;
  signatureStatus: string;
  editorName?: string;
  rejectReason?: string | null;
  isImporterSignedPO: boolean;
  isImporterSignedSC: boolean;
  isExporterSignedPO: boolean;
  isExporterSignedSC: boolean;
  importerPOSignMedia?: any;
  importerScSignMedia?: any;
  exporterPOSignMedia?: any;
  exporterScSignMedia?: any;
  updatedAt: string;
}

export interface ContractCycle {
  startIndex: number;
  endIndex: number;
  histories: SignatureHistory[];
  flow: FlowType;
  cycleNumber: number;
}

/**
 * 계약 플로우 타입을 판단합니다 (전체 히스토리 기반)
 * @param histories - 서명 히스토리 배열
 * @param defaultFlow - 기본 플로우 타입
 * @returns 플로우 타입 (EXPORTER_LEAD | IMPORTER_LEAD)
 */
export const determineContractFlow = (
  histories: SignatureHistory[] | null | undefined,
  defaultFlow: FlowType = "EXPORTER_LEAD",
): FlowType => {
  if (!histories || histories.length === 0) return defaultFlow;

  // 히스토리를 최신순으로 정렬 (updatedAt 기준 내림차순)
  const sortedHistories = [...histories].sort((a, b) => 
    new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );

  // 가장 최근 LINKED 상태의 인덱스 찾기
  const lastLinkedIndex = sortedHistories.findIndex(
    (history) => history.signatureStatus === "LINKED"
  );

  // LINKED 이후의 히스토리만 고려 (LINKED가 없으면 전체 히스토리 사용)
  const relevantHistories = lastLinkedIndex >= 0 
    ? sortedHistories.slice(0, lastLinkedIndex) // LINKED 이전까지의 히스토리
    : sortedHistories;

  // 관련 히스토리가 없으면 기본값 반환
  if (relevantHistories.length === 0) return defaultFlow;

  // 가장 오래된 히스토리를 명시적으로 찾기
  const oldestHistory = relevantHistories.reduce((oldest, current) =>
    new Date(oldest.updatedAt) < new Date(current.updatedAt) ? oldest : current,
  );

  // SC가 먼저 생성되었으면 수출자 주도
  if (oldestHistory.exporterScSignMedia && !oldestHistory.importerPOSignMedia) {
    return "EXPORTER_LEAD";
  }
  // PO가 먼저 생성되었으면 수입자 주도
  if (oldestHistory.importerPOSignMedia && !oldestHistory.exporterScSignMedia) {
    return "IMPORTER_LEAD";
  }

  return defaultFlow;
};

/**
 * 현재 계약 단계를 판단합니다 (전체 히스토리 기반)
 * @param histories - 서명 히스토리 배열
 * @param flow - 플로우 타입
 * @returns 현재 단계 (1 | 2 | 3)
 */
export const determineContractStage = (
  histories: SignatureHistory[] | null | undefined,
  flow: FlowType,
): 1 | 2 | 3 => {
  if (!histories || histories.length === 0) return 1;

  // 최신 히스토리에서 서명 완료 상태 확인
  const latestHistory = histories[0];
  const isPOComplete =
    latestHistory.isImporterSignedPO && latestHistory.isExporterSignedPO;
  const isSCComplete =
    latestHistory.isImporterSignedSC && latestHistory.isExporterSignedSC;

  if (isPOComplete && isSCComplete) return 3;

  if (flow === "EXPORTER_LEAD") {
    return isSCComplete ? 2 : 1;
  } else {
    return isPOComplete ? 2 : 1;
  }
};

/**
 * 특정 단계에서 활성화된 문서 타입을 반환합니다
 * @param flow - 플로우 타입
 * @param stage - 단계 (1 | 2 | 3)
 * @returns 문서 타입 ("SC" | "PO" | null)
 */
export const getActiveDocumentForStage = (
  flow: FlowType,
  stage: number,
): "SC" | "PO" | null => {
  if (flow === "EXPORTER_LEAD") {
    return stage === 1 ? "SC" : stage === 2 ? "PO" : null;
  } else {
    return stage === 1 ? "PO" : stage === 2 ? "SC" : null;
  }
};

/**
 * 히스토리 항목이 속한 단계를 판단합니다
 * @param history - 단일 히스토리 항목
 * @param flow - 플로우 타입
 * @returns 히스토리가 속한 단계 (1 | 2 | 3)
 */
export const getHistoryStage = (
  history: SignatureHistory,
  flow: FlowType,
): 1 | 2 | 3 => {
  // SC 완료 상태
  const isSCComplete = history.isImporterSignedSC && history.isExporterSignedSC;
  // PO 완료 상태
  const isPOComplete = history.isImporterSignedPO && history.isExporterSignedPO;

  if (flow === "EXPORTER_LEAD") {
    // SC가 완료되지 않았으면 1단계
    if (!isSCComplete) return 1;
    // SC는 완료되었지만 PO가 완료되지 않았으면 2단계
    if (isSCComplete && !isPOComplete) return 2;
    // 둘 다 완료되었으면 3단계
    return 3;
  } else {
    // PO가 완료되지 않았으면 1단계
    if (!isPOComplete) return 1;
    // PO는 완료되었지만 SC가 완료되지 않았으면 2단계
    if (isPOComplete && !isSCComplete) return 2;
    // 둘 다 완료되었으면 3단계
    return 3;
  }
};

/**
 * 히스토리 항목의 문서 타입을 판단합니다
 * @param history - 단일 히스토리 항목
 * @param flow - 플로우 타입
 * @returns 문서 타입 ("SC" | "PO" | null)
 */
export const getHistoryDocumentType = (
  history: SignatureHistory,
  flow: FlowType,
): "SC" | "PO" | null => {
  // 더 정확한 판단을 위해 실제 서명 활동을 확인
  
  // SC 관련 활동이 있는지 확인
  const hasScActivity = 
    history.exporterScSignMedia || 
    history.importerScSignMedia ||
    history.isExporterSignedSC || 
    history.isImporterSignedSC;
    
  // PO 관련 활동이 있는지 확인  
  const hasPoActivity = 
    history.importerPOSignMedia || 
    history.exporterPOSignMedia ||
    history.isImporterSignedPO || 
    history.isExporterSignedPO;
  
  // 최신 활동 확인 - 가장 최근에 변경된 문서 타입 반환
  // 두 가지 모두 활동이 있을 경우, 단계를 기반으로 판단
  if (hasScActivity && hasPoActivity) {
    const historyStage = getHistoryStage(history, flow);
    return getActiveDocumentForStage(flow, historyStage);
  }
  
  // SC 활동만 있는 경우
  if (hasScActivity && !hasPoActivity) {
    return "SC";
  }
  
  // PO 활동만 있는 경우
  if (hasPoActivity && !hasScActivity) {
    return "PO";
  }
  
  // 활동이 없는 경우, 단계 기반으로 판단 (fallback)
  const historyStage = getHistoryStage(history, flow);
  return getActiveDocumentForStage(flow, historyStage);
};

/**
 * Lead/Follow 역할을 판단합니다 (전체 계약 기준)
 * @param histories - 서명 히스토리 배열
 * @param userRole - 사용자 역할 (EXPORTER | IMPORTER)
 * @returns Lead 역할 여부
 */
export const isLeadRole = (
  histories: SignatureHistory[] | null | undefined,
  userRole: "EXPORTER" | "IMPORTER",
): boolean => {
  const flow = determineContractFlow(histories);
  return (
    (flow === "EXPORTER_LEAD" && userRole === "EXPORTER") ||
    (flow === "IMPORTER_LEAD" && userRole === "IMPORTER")
  );
};

/**
 * 특정 단계에서의 Lead/Follow 역할을 판단합니다
 * @param histories - 서명 히스토리 배열
 * @param userRole - 사용자 역할 (EXPORTER | IMPORTER)
 * @param stage - 확인할 단계 (1 | 2 | 3)
 * @returns 해당 단계에서의 Lead 역할 여부
 */
export const isLeadRoleForStage = (
  histories: SignatureHistory[] | null | undefined,
  userRole: "EXPORTER" | "IMPORTER",
  stage: 1 | 2 | 3,
): boolean => {
  const flow = determineContractFlow(histories);
  const activeDoc = getActiveDocumentForStage(flow, stage);
  
  if (!activeDoc) return false; // Stage 3에서는 lead 개념이 없음
  
  // 각 단계에서 누가 주도하는지 판단
  if (flow === "EXPORTER_LEAD") {
    // 수출자 주도 플로우: 1단계 SC(수출자), 2단계 PO(수입자)
    if (stage === 1) {
      return userRole === "EXPORTER"; // SC는 수출자가 주도
    } else if (stage === 2) {
      return userRole === "IMPORTER"; // PO는 수입자가 주도
    }
  } else {
    // 수입자 주도 플로우: 1단계 PO(수입자), 2단계 SC(수출자)
    if (stage === 1) {
      return userRole === "IMPORTER"; // PO는 수입자가 주도
    } else if (stage === 2) {
      return userRole === "EXPORTER"; // SC는 수출자가 주도
    }
  }
  
  return false;
};

/**
 * 현재 계약 상태에서의 Lead/Follow 역할을 판단합니다
 * @param histories - 서명 히스토리 배열
 * @param userRole - 사용자 역할 (EXPORTER | IMPORTER)
 * @returns 현재 상태에서의 Lead 역할 여부
 */
export const isCurrentLeadRole = (
  histories: SignatureHistory[] | null | undefined,
  userRole: "EXPORTER" | "IMPORTER",
): boolean => {
  const flow = determineContractFlow(histories);
  const currentStage = determineContractStage(histories, flow);
  
  return isLeadRoleForStage(histories, userRole, currentStage);
};
